import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { useFormDraft } from "@/hooks/useFormDraft";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { CheckCircle2, Loader2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useTransactionsSupabaseStore } from "@/store/api/transactionsSupabaseStore";
import { useUsersSupabaseStore } from "@/store/api/usersSupabaseStore";
import { useUserStore } from "@/store/userStore";
import { useToast } from "@/hooks/use-toast";
import { useClientSubscriptionStore } from "@/store/api/clients/clientSubscriptionStore";
import { useCurrency } from "@/contexts/CurrencyContext";
import { checkOverpayment, completedTotal } from "@/lib/payments/paymentRules";
import { ClientPicker } from "../clients/ClientPicker";
import { Subscription } from "@/types/subscription";

interface PaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const formSchema = z.object({
  client: z.string().min(2, { message: "Please select a client" }),
  amount: z.string().refine(
    (val) => !isNaN(Number(val)) && Number(val) > 0,
    { message: "Amount must be a positive number" }
  ),
  status: z.string(),
  paymentMethod: z.string(),
  description: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

export function PaymentDialog({
  open,
  onOpenChange,
}: PaymentDialogProps) {
  const { t } = useTranslation(["payments", "common", "subscriptions"]);
  const { formatCurrency } = useCurrency();
  const isMobile = useIsMobile();
  const [isSuccess, setIsSuccess] = useState(false);
  const { addTransaction, isLoading } = useTransactionsSupabaseStore();
  const { isLoading: isSubscriptionLoading, loadClientSubscriptionDataForUser} = useClientSubscriptionStore();
  const { users, fetchUsers, isLoading: usersLoading, error: usersError } = useUsersSupabaseStore();
  const { toast } = useToast();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [totalPaid, setTotalPaid] = useState<number | null>(null);
  const [remainingBalance, setRemainingBalance] = useState<number | null>(null);
  const [loadingBalance, setLoadingBalance] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription>(null);

  const refreshBalance = async (userId: string) => {
    try {
      setLoadingBalance(true);
      // Réinitialisation systématique AVANT le chargement : sans elle, changer
      // pour un client sans abonnement laissait `selectedSubscription` et
      // `totalPaid` du client précédent, et le paiement était rattaché à
      // l'abonnement d'un autre adhérent.
      setRemainingBalance(null);
      setTotalPaid(null);
      setSelectedSubscription(null);

      const subscriptionData = await loadClientSubscriptionDataForUser(userId);
      const activeSubscription = subscriptionData?.subscription ?? null;
      if (!activeSubscription) {
        return;
      }

      // Seuls les paiements encaissés réduisent le reste à payer.
      const payments = (subscriptionData.paymentsBySub[activeSubscription.id] ?? []) as Array<{
        status?: string | null;
        amount?: number | null;
      }>;
      const totalPaid = completedTotal(payments);
      const remain = (activeSubscription.price ?? 0) - totalPaid;
      setTotalPaid(totalPaid);
      setRemainingBalance(remain);
      setSelectedSubscription(activeSubscription);
    } catch (e) {
      console.error(e);
      setRemainingBalance(null);
      setTotalPaid(null);
      setSelectedSubscription(null);

    } finally {
      setLoadingBalance(false);
    }
  };
  
  // Auto-scroll to top when dialog opens (pour petits écrans ou si form long)
  useEffect(() => {
    if (open && scrollRef.current) {
      setTimeout(() => {
        scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      }, 120);
    }
  }, [open]);

  // Charger les clients de la salle connectée à l'ouverture et à chaque
  // changement de salle ; un client sélectionné d'une autre salle est retiré.
  const gymId = useUserStore((s) => s.gymId);
  const prevGymIdRef = useRef<string | null>(gymId);
  useEffect(() => {
    if (prevGymIdRef.current !== gymId) {
      prevGymIdRef.current = gymId;
      form.setValue("client", "");
      setSelectedSubscription(null);
      setTotalPaid(null);
      setRemainingBalance(null);
    }
    if (open && gymId) {
      void fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, gymId, fetchUsers]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      client: "",
      amount: "",
      status: "completed",
      paymentMethod: "card",
      description: "",
    },
  });

  // Brouillon persisté. Le dialog est monté en permanence et sa fermeture
  // blanchit les champs : la restauration passe par l'effet d'ouverture
  // ci-dessous, pas par le mount.
  const { clearDraft, mergeDraftOver } = useFormDraft<FormValues>("payment-form", form, {
    restoreOnMount: false,
    isEmpty: (v) => !v.client && !v.amount && !v.description,
  });

  // À l'ouverture : superposer un éventuel brouillon et — indispensable —
  // relancer refreshBalance pour le client restauré, sans quoi
  // selectedSubscription reste nul et le submit est refusé.
  useEffect(() => {
    if (!open) return;
    const merged = mergeDraftOver(form.getValues());
    form.reset(merged);
    if (merged.client) void refreshBalance(merged.client);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onClientChange = (id: string) => {
    form.setValue("client", id, { shouldValidate: true });
    void refreshBalance(id);
  };

  const onSubmit = async (data: FormValues) => {
    try {
      const userId = data.client;
       
      if (!userId) {
        toast({
          title: t("common:error"),
          description: t("common:clientNotFound"),
          variant: "destructive",
        });
        return;
      }

      const userObj = users.find(u => u.id === userId);
      const clientName = userObj ? `${userObj.firstName} ${userObj.lastName}` : "";

        if(!selectedSubscription){
          toast({
            title: t("noActiveSubscriptionFound", {ns:"subscriptions"}),
            description: t("noActiveSubscriptionFoundDesc",  { client :clientName, ns:"subscriptions"}),
            variant: "destructive",
          });
          return;
        }
        const transaction = {
          userId: userId,
          client: data.client,
          amount: parseFloat(data.amount),
          date: new Date().toISOString().split('T')[0],
          status: data.status,
          paymentMethod: data.paymentMethod,
          type: "Payment",
          description: data.description || "Payment",
          subscriptionId : selectedSubscription.id
        };

      const amountNum = parseFloat(data.amount || "0");
      const overCheck = checkOverpayment(totalPaid, amountNum, selectedSubscription.price);
      if (overCheck.ok === false) {
        const overflow = overCheck.overflow;
        toast({
          title: t("payments:overpaymentTitle", "Montant trop élevé"),
          description: t("payments:overpaymentDesc", { rest: overflow }),
          variant: "destructive",
        });
        return;
      }

        const success = await addTransaction(transaction);
        if (!success) {
          toast({
            title: t("newTransaction.errorMessage"),
            description: t("common:tryAgain"),
            variant: "destructive",
          });
          return;
        }
      
      setIsSuccess(true);
      clearDraft();
      toast({
        title: t("newTransaction.successMessage"),
        description: t("newTransaction.generateInvoice"),
      });

      setTimeout(() => {
        setIsSuccess(false);
        form.reset();
        setSelectedSubscription(null);
        setRemainingBalance(null);
        onOpenChange(false);
      }, 1500);
    } catch (error) {
      console.error("Payment submission error:", error);
      toast({
        title: t("newTransaction.errorMessage"),
        description: error,
        variant: "destructive",
      });
    }
  };

  const handleDialogChange = (open: boolean) => {
    if (!open) {
      form.reset();
      setIsSuccess(false);
      // Même raison qu'au changement de client : à la réouverture, l'abonnement
      // du client précédent ne doit pas être encore sélectionné.
      setSelectedSubscription(null);
      setTotalPaid(null);
      setRemainingBalance(null);
    }
    onOpenChange(open);
  };

  // --- Rendu ---
  // Le corps et le pied sont partagés entre les deux conteneurs :
  // Dialog centré sur desktop, bottom sheet sur mobile (pattern
  // CoachDailySchedule). Tout le state (RHF, brouillon, solde) vit ici,
  // au-dessus du ternaire, donc il survit au changement de conteneur.
  const successBlock = (
    <div className="py-8 flex flex-col items-center text-center space-y-3">
      <CheckCircle2 className="h-12 w-12 text-green-500" />
      <h3 className="text-lg font-medium">{t("newTransaction.successMessage")}</h3>
      <p className="text-sm text-gray-500">{t("newTransaction.generateInvoice")}</p>
    </div>
  );

  // text-base (16px) sur mobile : en dessous, iOS zoome le champ au focus.
  const formBlock = (
    <Form {...form}>
      <form id="payment-dialog-form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-3 pb-4">
        <FormField
          control={form.control}
          name="client"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("newTransaction.clientName")}</FormLabel>
              <FormControl>
                <ClientPicker
                  users={users}
                  value={field.value}
                  onChange={onClientChange}
                  placeholder={t("newTransaction.selectClient")}
                  disabled={isLoading || usersLoading}
                />
              </FormControl>
              {usersError && !usersLoading && (
                <p className="text-sm text-destructive flex items-center gap-2">
                  {t("errors.fetchError", { ns: "common" })}
                  <button type="button" className="underline" onClick={() => void fetchUsers()}>
                    {t("retry", { ns: "common" })}
                  </button>
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Encadré solde restant */}
        {selectedSubscription ? (
          <div className="text-sm rounded-md border p-3 bg-muted/30">
            {loadingBalance ? (
              <span className="opacity-70">{t("common:loading")}</span>
            ) : remainingBalance == null ? (
              <span className="opacity-70">{t("subscriptions:noActiveSubscription", { ns: "athlete" })}</span>
            ) : remainingBalance <= 0 ? (
              <span className="text-green-600 font-medium">{t("payments:fullyPaid", "Abonnement déjà soldé")}</span>
            ) : (
              <span>
                {t("payments:remainingBalance", "Reste à payer")}: <b>{formatCurrency(remainingBalance)}</b>
              </span>
            )}
          </div>
        ) : (
          <div className="text-sm rounded-md border p-3 bg-muted/30">
            <span className="opacity-70">{t("subscriptions:noActiveSubscription", { ns: "athlete" })}</span>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-2">
          <FormField
            control={form.control}
            name="amount"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormLabel>{t("newTransaction.amount")}</FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    placeholder="0.00"
                    {...field}
                    disabled={isLoading}
                    className="w-full text-base md:text-sm"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="paymentMethod"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormLabel>{t("newTransaction.paymentMethod")}</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  disabled={isLoading}
                >
                  <FormControl>
                    <SelectTrigger className="w-full text-base md:text-sm">
                      <SelectValue placeholder={t("newTransaction.selectMethod")} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="card">{t("methods.card")}</SelectItem>
                    <SelectItem value="cash">{t("methods.cash")}</SelectItem>
                    <SelectItem value="bank_transfer">{t("methods.transfer")}</SelectItem>
                    <SelectItem value="check">{t("methods.check")}</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormLabel>{t("status")}</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  disabled={isLoading}
                >
                  <FormControl>
                    <SelectTrigger className="w-full text-base md:text-sm">
                      <SelectValue placeholder={t("filterByStatus")} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="completed">{t("statuses.completed")}</SelectItem>
                    <SelectItem value="pending">{t("statuses.pending")}</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("newTransaction.description")}</FormLabel>
              <FormControl>
                <Textarea
                  placeholder={t("newTransaction.descriptionPlaceholder")}
                  className="resize-none min-h-[70px] text-base md:text-sm"
                  {...field}
                  disabled={isLoading}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );

  // Footer STICKY (hors zone scrollable) : le bouton de validation reste
  // visible même formulaire long / clavier ouvert. Le submit référence le
  // <form> par id puisqu'il vit en dehors.
  const footerBlock = !isSuccess && (
    <div className="shrink-0 border-t bg-background p-4 pb-[max(1rem,env(safe-area-inset-bottom))] flex flex-row gap-2">
      {/* Annulation explicite : le brouillon est jeté (la simple
          fermeture Escape/overlay le conserve). */}
      <Button
        type="button"
        variant="outline"
        onClick={() => { clearDraft(); onOpenChange(false); }}
        disabled={isLoading || isSubscriptionLoading}
        className="flex-1 h-10"
      >
        {t("common:cancel")}
      </Button>
      <Button
        type="submit"
        form="payment-dialog-form"
        disabled={isLoading || isSubscriptionLoading}
        className="flex-1 h-10"
      >
        {(isLoading || isSubscriptionLoading) ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {t("common:processing")}
          </>
        ) : (
          t("newTransaction.processPayment")
        )}
      </Button>
    </div>
  );

  const bodyBlock = isSuccess ? successBlock : formBlock;

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={handleDialogChange}>
        <SheetContent side="bottom" className="rounded-t-3xl p-0 flex flex-col max-h-[92dvh]">
          <div className="mx-auto mt-2 h-1.5 w-10 rounded-full bg-muted" aria-hidden />
          <SheetHeader className="shrink-0 px-4 pt-2 pb-2 text-left">
            <SheetTitle className="text-xl">{t("newTransaction.title")}</SheetTitle>
            <SheetDescription className="text-xs">{t("newTransaction.descriptionNewPayment")}</SheetDescription>
          </SheetHeader>
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-2">
            {bodyBlock}
          </div>
          {footerBlock}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleDialogChange}>
      <DialogContent className="sm:max-w-[425px] p-0 flex flex-col max-h-[90dvh] overflow-hidden">
        <DialogHeader className="shrink-0 bg-background px-6 pt-4 pb-2">
          <DialogTitle className="text-xl sm:text-2xl">{t("newTransaction.title")}</DialogTitle>
          <DialogDescription className="text-xs sm:text-sm mb-0">{t("newTransaction.descriptionNewPayment")}</DialogDescription>
        </DialogHeader>
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-2">
          {bodyBlock}
        </div>
        {footerBlock}
      </DialogContent>
    </Dialog>
  );
}
